import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, Mail, CheckCircle, Calendar } from "lucide-react";
import { differenceInDays, format } from "date-fns";
import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

export default function FollowUpTracker({ investors }) {
  const [sendingEmail, setSendingEmail] = useState(null);

  const sendEmailMutation = useMutation({
    mutationFn: async ({ investor, message }) => {
      return await base44.integrations.Core.SendEmail({
        to: investor.contact_email,
        subject: `Oppfølging: ${investor.name}`,
        body: message,
      });
    },
    onSuccess: () => {
      toast.success("E-post sendt!");
      setSendingEmail(null);
    },
    onError: () => {
      toast.error("Kunne ikke sende e-post");
      setSendingEmail(null);
    },
  });

  const updateLastContactMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Investor.update(id, data),
    onSuccess: () => {
      toast.success("Sist kontakt oppdatert!");
    },
  });

  // Kategoriser investorer basert på status og siste kontakt
  const getFollowUpInvestors = () => {
    const today = new Date();
    
    return investors
      .filter(inv => inv.status !== "exited") // Ikke vis eksiterte
      .map(inv => {
        const lastContact = inv.last_contact_date ? new Date(inv.last_contact_date) : null;
        const daysSinceContact = lastContact ? differenceInDays(today, lastContact) : 999;
        
        let priority = "low";
        let reason = "";
        
        // Bestem prioritet basert på status og tid siden sist kontakt
        if (inv.status === "prospect" && daysSinceContact > 14) {
          priority = "high";
          reason = `Ingen kontakt på ${daysSinceContact} dager`;
        } else if (inv.status === "in_discussion" && daysSinceContact > 7) {
          priority = "urgent";
          reason = `I diskusjon, ${daysSinceContact} dager siden kontakt`;
        } else if (inv.status === "committed" && daysSinceContact > 3) {
          priority = "urgent";
          reason = `Forpliktet, følg opp (${daysSinceContact} dager)`;
        } else if (daysSinceContact > 30) {
          priority = "medium";
          reason = `Lang tid siden kontakt (${daysSinceContact} dager)`;
        } else if (!lastContact) {
          priority = "medium";
          reason = "Ingen kontaktdato registrert";
        }
        
        return {
          ...inv,
          daysSinceContact,
          priority,
          reason,
          needsFollowUp: priority !== "low",
        };
      })
      .filter(inv => inv.needsFollowUp)
      .sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });
  };

  const followUpList = getFollowUpInvestors();

  const priorityColors = {
    urgent: "bg-red-100 text-red-700 border-red-200",
    high: "bg-orange-100 text-orange-700 border-orange-200",
    medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
    low: "bg-green-100 text-green-700 border-green-200",
  };

  const handleSendReminder = (investor) => {
    const message = `Hei,\n\nDette er en påminnelse om å følge opp ${investor.name}.\n\nStatus: ${investor.status?.replace("_", " ")}\nSist kontakt: ${investor.last_contact_date ? format(new Date(investor.last_contact_date), "dd.MM.yyyy") : "Ikke registrert"}\n\nGrunn: ${investor.reason}\n\nVennlig hilsen`;
    
    setSendingEmail(investor.id);
    sendEmailMutation.mutate({ investor, message });
  };

  const handleMarkContacted = (investor) => {
    updateLastContactMutation.mutate({
      id: investor.id,
      data: { last_contact_date: new Date().toISOString().split("T")[0] },
    });
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
          <Bell className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Oppfølging Påkrevd</h3>
          <p className="text-sm text-slate-500">{followUpList.length} investorer trenger oppfølging</p>
        </div>
      </div>

      {followUpList.length === 0 ? (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            Alle investorer er oppdaterte! Ingen oppfølging nødvendig akkurat nå.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {followUpList.map((investor) => (
            <div
              key={investor.id}
              className={`p-4 rounded-lg border ${priorityColors[investor.priority]}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-slate-900">{investor.name}</h4>
                    <Badge className="text-xs border-0 bg-white/50">
                      {investor.priority === "urgent" ? "⚠️ Haster" : investor.priority === "high" ? "Høy" : "Medium"}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{investor.reason}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span>📧 {investor.contact_email}</span>
                    {investor.responsible_person_name && (
                      <span>👤 {investor.responsible_person_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSendReminder(investor)}
                    disabled={sendingEmail === investor.id}
                    className="gap-2"
                  >
                    <Mail className="w-3 h-3" />
                    {sendingEmail === investor.id ? "Sender..." : "Send påminnelse"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleMarkContacted(investor)}
                    className="gap-2"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Marker kontaktet
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}